import { useToast } from '@mdvip/shared/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import type { ReceiptAction } from '@/components/payments/transactions/TransactionRow';
import type { PaymentTransaction } from '@/hooks/payments/transactions/transactionsApi';
import { downloadReceiptFile } from '@/lib/payments/receiptDownload';
import { useDownloadPaymentReceipt } from './useDownloadPaymentReceipt';
import { useSendPaymentReceiptEmail } from './useSendPaymentReceiptEmail';

/**
 * The whole Generate Receipt flow behind one callback, ready to hand to a transaction row.
 *
 * Owns the success side of both operations — the confirmation toasts and, for the PDF, handing the
 * bytes to the browser. Each operation hook owns its own failure toast, so that its Retry re-fires
 * the operation that actually failed.
 *
 * Every toast in this flow is dismissible, matching the design, which shows a close button on all
 * three. The shared Toaster leaves it off by default, so each toast opts in.
 */
export function useGenerateReceipt() {
  const { toast } = useToast();
  const { t } = useTranslation('members');

  const email = useSendPaymentReceiptEmail({
    onSuccess: (result) => {
      toast({
        title: t('payments.transactions.receipt.emailSuccess.title'),
        // The server's address, never a client-known one: it is the only proof of where this went.
        description: t('payments.transactions.receipt.emailSuccess.description', { email: result.email }),
        variant: 'success',
        closeButton: true,
      });
    },
  });

  const download = useDownloadPaymentReceipt({
    onSuccess: (result) => {
      downloadReceiptFile(result.document, result.filename);
      toast({
        title: t('payments.transactions.receipt.pdfSuccess.title'),
        description: result.filename,
        variant: 'success',
        closeButton: true,
      });
    },
  });

  /** Whether either operation is already working on this row. */
  const isPending = (transactionId: string) =>
    email.pendingIds.has(transactionId) || download.pendingIds.has(transactionId);

  return {
    onGenerateReceipt: (transaction: PaymentTransaction, action: ReceiptAction) => {
      // The row's menu items are disabled while it is busy, but the guard is repeated here so the
      // no-duplicate-request invariant survives a caller that renders its own trigger.
      if (isPending(transaction.id)) {
        return;
      }
      if (action === 'email') {
        email.submit(transaction.id);
      } else {
        download.submit(transaction.id);
      }
    },
    isPending,
  };
}
